import { Button } from "@ascleo/ui/components/ui/button";
import { eligibleContactKind } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
import { ContactAssertion } from "@ascleo/frontdesk-contracts/patient-info/submit";
import { Schema } from "effect";
import { PenLine, Send, Undo2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { KioskTextField } from "../../../../../../components/kiosk-field";
import { ProtectedChip } from "./ContactRow";
import { maskValue } from "./masking";
import { proposalFieldName } from "./staged-changes";
import type { ReviewForm } from "./use-review-form";

/**
 * A protected contact inside the editor: read-only, with a way to propose a replacement.
 *
 * The practice's record is never overwritten from here. A proposal is an ordinary edit on the wire
 * that the server routes to a reception task, and the row says so *before* the patient commits —
 * otherwise someone types a new address, taps save, and reasonably believes it took effect.
 *
 * The protection reason (`confirmed`, `portal-linked`, …) is internal and never rendered: every
 * protected contact looks the same to the patient.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <ProtectedProposalRow contact={contact} form={form} onDiscard={() => {}} />
 * ```
 */
export function ProtectedProposalRow({
  contact,
  form,
  onDiscard,
}: {
  contact: ReviewContact;
  form: ReviewForm;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const [proposing, setProposing] = useState(false);
  const invalidKey =
    eligibleContactKind(contact.type) === "email"
      ? "self-service.check-in.patient-info.review.invalid-email"
      : "self-service.check-in.patient-info.review.invalid-phone";

  return (
    <div
      className="flex min-w-0 flex-col gap-3 border-t pt-4"
      data-testid="patient-info-protected-row"
    >
      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center md:gap-4">
        <span className="text-base text-foreground md:w-[190px] md:shrink-0">
          {t(`self-service.check-in.patient-info.review.type.${contact.type}`)}
        </span>
        <span className="min-w-0 flex-1 text-xl break-all text-foreground">
          {maskValue(contact.value)}
        </span>
        <ProtectedChip />
      </div>

      {proposing ? (
        <form.Field
          name={proposalFieldName(contact.itemId)}
          validators={{ onChange: Schema.toStandardSchemaV1(ContactAssertion) }}
        >
          {(field) => (
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-start">
                <div className="min-w-0 flex-1">
                  <KioskTextField
                    id={`patient-info-proposal-${contact.itemId}`}
                    testId="patient-info-proposal-input"
                    errorTestId="patient-info-proposal-error"
                    label={t("self-service.check-in.patient-info.review.proposal-placeholder")}
                    icon={PenLine}
                    inputMode={
                      eligibleContactKind(contact.type) === "email" ? "email" : "tel"
                    }
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors.length > 0 ? t(invalidKey) : undefined}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="2xl"
                  className="self-start"
                  onClick={() => {
                    field.handleChange("");
                    setProposing(false);
                    onDiscard();
                  }}
                  data-testid="patient-info-proposal-discard-button"
                >
                  <Undo2 aria-hidden className="size-6" />
                  {t("self-service.check-in.patient-info.review.proposal-discard")}
                </Button>
              </div>
              <p className="m-0 flex items-start gap-2 text-lg text-foreground">
                <Send className="mt-1 size-5 shrink-0 text-primary" aria-hidden="true" />
                {t("self-service.check-in.patient-info.review.proposal-hint")}
              </p>
            </div>
          )}
        </form.Field>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="2xl"
          className="w-fit"
          onClick={() => setProposing(true)}
          data-testid="patient-info-proposal-open-button"
        >
          <PenLine aria-hidden className="size-6" />
          {t("self-service.check-in.patient-info.review.proposal-open")}
        </Button>
      )}
    </div>
  );
}
